import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface GenerationProgress {
  stage: 'text_generation' | 'audio_generation' | 'uploading';
  progress: number;
  message: string;
}

interface StoryStatus {
  story_id: string;
  title: string;
  status: string;
  progress: GenerationProgress | null;
  generation_error: string | null;
}

interface Props {
  storyId: string;
  onComplete: () => void;
  onError: (error: string) => void;
}

export default function StoryGenerationProgress({ storyId, onComplete, onError }: Props) {
  const [status, setStatus] = useState<StoryStatus | null>(null);
  const [isPolling, setIsPolling] = useState(true);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const pollStatus = async () => {
      try {
        const data = await api.getStory(storyId);
        setStatus(data);

        // Check if generation is complete
        if (data.status === 'completed') {
          setIsPolling(false);
          onComplete();
        } else if (data.status === 'failed') {
          setIsPolling(false);
          onError(data.generation_error || 'Unknown error occurred');
        }
      } catch (error) {
        console.error('Error polling story status:', error);
        // Continue polling even on error
      }
    };

    if (isPolling) {
      // Poll immediately
      pollStatus();

      // Then poll every 2 seconds
      intervalId = setInterval(pollStatus, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [storyId, isPolling, onComplete, onError]);

  if (!status) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'text_generation':
        return '📝';
      case 'audio_generation':
        return '🎙️';
      case 'uploading':
        return '☁️';
      default:
        return '⏳';
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="bg-white rounded-3xl shadow-soft-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold text-primary-500 mb-2">
            Creating Your Story
          </h2>
          <p className="text-text-medium">
            {status.title}
          </p>
        </div>

        {/* Progress Stages */}
        <div className="space-y-6 mb-8">
          {/* Text Generation Stage */}
          <div className={`flex items-center ${status.progress?.stage === 'text_generation' ? 'opacity-100' : status.progress && ['audio_generation', 'uploading'].includes(status.progress.stage) ? 'opacity-50' : 'opacity-30'}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
              status.progress && ['audio_generation', 'uploading'].includes(status.progress.stage)
                ? 'bg-green-100'
                : status.progress?.stage === 'text_generation'
                ? 'bg-primary-100 animate-pulse'
                : 'bg-gray-100'
            }`}>
              {status.progress && ['audio_generation', 'uploading'].includes(status.progress.stage) ? '✓' : '📝'}
            </div>
            <div className="ml-4 flex-1">
              <p className="font-semibold text-text-dark">Generating Story</p>
              <p className="text-sm text-text-light">Writing your personalized therapeutic story...</p>
            </div>
          </div>

          {/* Audio Generation Stage */}
          <div className={`flex items-center ${status.progress?.stage === 'audio_generation' ? 'opacity-100' : status.progress?.stage === 'uploading' ? 'opacity-50' : 'opacity-30'}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
              status.progress?.stage === 'uploading'
                ? 'bg-green-100'
                : status.progress?.stage === 'audio_generation'
                ? 'bg-primary-100 animate-pulse'
                : 'bg-gray-100'
            }`}>
              {status.progress?.stage === 'uploading' ? '✓' : '🎙️'}
            </div>
            <div className="ml-4 flex-1">
              <p className="font-semibold text-text-dark">Creating Audiobook</p>
              <p className="text-sm text-text-light">
                {status.progress?.stage === 'audio_generation'
                  ? status.progress.message
                  : 'Converting story to speech...'}
              </p>
              {/* Progress Bar for Audio Generation */}
              {status.progress?.stage === 'audio_generation' && (
                <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary-500 h-full transition-all duration-300"
                    style={{ width: `${status.progress.progress}%` }}
                  ></div>
                </div>
              )}
            </div>
            {status.progress?.stage === 'audio_generation' && (
              <div className="ml-4 text-primary-500 font-bold">
                {status.progress.progress}%
              </div>
            )}
          </div>

          {/* Uploading Stage */}
          <div className={`flex items-center ${status.progress?.stage === 'uploading' ? 'opacity-100' : 'opacity-30'}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
              status.progress?.stage === 'uploading'
                ? 'bg-primary-100 animate-pulse'
                : 'bg-gray-100'
            }`}>
              ☁️
            </div>
            <div className="ml-4 flex-1">
              <p className="font-semibold text-text-dark">Uploading</p>
              <p className="text-sm text-text-light">Saving your audiobook...</p>
            </div>
          </div>
        </div>

        {/* Current Status Message */}
        {status.progress && (
          <div className="bg-primary-50 rounded-xl p-4 text-center">
            <p className="text-primary-800">
              <span className="text-2xl mr-2">{getStageIcon(status.progress.stage)}</span>
              {status.progress.message}
            </p>
          </div>
        )}

        {/* Spinner */}
        <div className="flex justify-center mt-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>

        <p className="text-center text-sm text-text-light mt-4">
          This usually takes 30-60 seconds. Please don't close this page.
        </p>
      </div>
    </div>
  );
}
